import { CalendarCheck2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import { isoDate } from '@/features/attendance/dates'
import type { Cover, Lesson, WeekData } from '../types'
import { indexLessons, lessonKey, nowAndNext, timeRange, weekdayName } from '../week'
import { LessonCard, type LessonView } from './LessonCard'

/**
 * Today's periods as a list, with the running and next ones flagged. For a teacher, the lessons of
 * covers active today are added, and their own lessons someone else covers today are marked.
 */
export function TodayCard({
  week,
  view,
  now = new Date(),
  covers = [],
  coveredBy = [],
}: {
  week: WeekData
  view: LessonView
  now?: Date
  covers?: Cover[]
  coveredBy?: Cover[]
}) {
  const { t } = useTranslation('timetable')
  const live = nowAndNext(week.days, week.periods, now)
  const iso = isoDate(now)
  const activeToday = (c: Cover) => c.startDate <= iso && iso <= c.endDate
  const coverLessons: Lesson[] = covers.filter(activeToday).flatMap((c) => c.lessons)
  const coverIds = new Set(coverLessons.map((l) => l.id))
  const lessons = indexLessons([...week.entries, ...coverLessons])
  const substituteFor = (lesson: Lesson) =>
    coveredBy.find(
      (c) =>
        activeToday(c) &&
        c.kind === 'lesson' &&
        c.section.id === lesson.section.id &&
        c.subject?.id === lesson.subject.id,
    )?.substitute.name
  const today = live.today
  const rows =
    today === null ? [] : week.periods.filter((p) => !p.isBreak && lessons.has(lessonKey(today, p.id)))
  return (
    <Card className="print:hidden">
      <CardHeader
        title={t('Today')}
        description={today === null ? t('No school today') : weekdayName(today)}
        icon={<CalendarCheck2 />}
      />
      {rows.length === 0 || today === null ? (
        <EmptyState compact icon={<CalendarCheck2 />} title={t('No lessons today')} />
      ) : (
        <ol className="space-y-2 p-4">
          {rows.map((p) => {
            const lesson = lessons.get(lessonKey(today, p.id))
            if (!lesson) return null
            const isNow = live.nowPeriodId === p.id
            const isNext = live.nextPeriodId === p.id
            const isCover = coverIds.has(lesson.id)
            const coveredByName = isCover ? undefined : substituteFor(lesson)
            return (
              <li key={p.id} className="flex gap-3">
                <div className="w-20 shrink-0 pt-2 text-xs">
                  <span className="block font-semibold text-ink">{p.name}</span>
                  <span className="block text-muted tabular">{timeRange(p)}</span>
                </div>
                <div
                  className={cn(
                    'min-w-0 flex-1 rounded-control',
                    isNow && 'ring-2 ring-brand-500 ring-offset-1 ring-offset-surface',
                  )}
                >
                  <LessonCard
                    lesson={lesson}
                    view={view}
                    chip={
                      <span className="flex flex-wrap justify-end gap-1">
                        {isCover ? (
                          <Badge tone="warning" size="sm">
                            {lesson.teacher
                              ? t('Cover for {{name}}', { name: lesson.teacher.name })
                              : t('Cover')}
                          </Badge>
                        ) : null}
                        {coveredByName ? (
                          <Badge tone="neutral" size="sm">
                            {t('Covered by {{name}}', { name: coveredByName })}
                          </Badge>
                        ) : null}
                        {isNow ? (
                          <Badge tone="brand" size="sm">
                            {t('Now')}
                          </Badge>
                        ) : isNext ? (
                          <Badge tone="info" size="sm">
                            {t('Next')}
                          </Badge>
                        ) : null}
                      </span>
                    }
                  />
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </Card>
  )
}
